import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import '@/App.css';
import Navbar from '@/components/sections/Navbar';
import Footer from '@/components/sections/Footer';
import FloatingWhatsApp from '@/components/FloatingWhatsApp';
import { Toaster } from '@/components/ui/sonner';
import HomePage from '@/pages/HomePage';
import { installGlobalAnalyticsListeners } from '@/lib/analytics';
import RouteAnalytics from '@/components/RouteAnalytics';

// Code-split secondary routes — keeps initial bundle small. Marketing pages,
// blog and legal pages are only fetched when user navigates to them.
const BlogIndex = lazy(() => import('@/pages/BlogIndex'));
const BlogPost = lazy(() => import('@/pages/BlogPost'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const CctvPage = lazy(() => import('@/pages/services/CctvPage'));
const LocationPage = lazy(() => import('@/pages/locations/LocationPage'));
const KontraktorPage = lazy(() => import('@/pages/KontraktorPage'));

// Static snapshots are served at /index.html URLs in production. Both URL
// forms must resolve to the same page after React takes over the static HTML.
const PAGE_ROUTES = [
  ['/', HomePage],
  ['/blog', BlogIndex],
  ['/blog/:slug', BlogPost],
  ['/about', AboutPage],
  ['/contact', ContactPage],
  ['/privacy-policy', PrivacyPage],
  ['/terms', TermsPage],
  ['/services/cctv-rumah', CctvPage],
  ['/services/wiring-rumah', CctvPage],
  ['/services/pasang-aircond', CctvPage],
  ['/kawasan/:slug', LocationPage],
  ['/kontraktor/:comboSlug', KontraktorPage],
];

// Minimal fallback while chunk loads — matches site background so users
// barely perceive a delay.
const RouteFallback = () => (
  <div className="min-h-[60vh] bg-white" aria-busy="true" data-testid="route-loading-state" />
);

function App() {
  useEffect(() => {
    installGlobalAnalyticsListeners();
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <RouteAnalytics />
        {/* Skip-to-content link for keyboard / screen-reader users */}
        <a
          href="#main-content"
          data-testid="skip-to-main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:bg-emerald-500 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
        >
          Langkau ke kandungan utama
        </a>
        <div className="App font-display antialiased text-slate-900 bg-white">
          <Navbar />
          <main id="main-content">
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {PAGE_ROUTES.flatMap(([path, Page]) => [
                  path,
                  path === '/' ? '/index.html' : `${path}/index.html`,
                ].map(routePath => <Route key={routePath} path={routePath} element={<Page />} />))}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <FloatingWhatsApp />
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </MotionConfig>
  );
}

export default App;
